<script setup>
// Nexus IA → Gateway (custom/app/controllers/api/v1/accounts/neuxs/gateway_controller.rb
// e modelos_controller.rb). As regras moram no servidor; a tela só mostra e
// repassa o motivo de cada recusa.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ModeloEditor from './ModeloEditor.vue';
import { createNexusApi, erroDaApi } from './api';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
});

const { t } = useI18n({ messages });
const api = computed(() => createNexusApi(props.accountId));

const carregando = ref(true);
const erro = ref('');
const integracoes = ref([]);
const templates = ref([]);
const escolhida = ref(null);
const formulario = ref(null);
const criado = ref(null);
const editando = ref(null);
const agindo = ref('');
const copiado = ref('');

const atual = computed(
  () => integracoes.value.find(i => i.id === escolhida.value) || null
);

const quando = iso =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '';

const carrega = async () => {
  carregando.value = true;
  try {
    const { data } = await api.value.gateway();
    integracoes.value = data.integracoes;
    templates.value = data.templates;
    if (!integracoes.value.some(i => i.id === escolhida.value)) {
      escolhida.value = integracoes.value[0]?.id ?? null;
    }
    erro.value = '';
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_NEXUS.GATEWAY.ERRO'));
  } finally {
    carregando.value = false;
  }
};
watch(() => props.accountId, carrega, { immediate: true });

const copia = async (texto, chave) => {
  await navigator.clipboard.writeText(texto);
  copiado.value = chave;
  setTimeout(() => {
    if (copiado.value === chave) copiado.value = '';
  }, 1500);
};

const abreFormulario = () => {
  const g = atual.value.gateway;
  formulario.value = {
    param_to: g?.param_to || 'numero',
    param_msg: g?.param_msg || 'mensagem',
    param_contrato: g?.param_contrato || 'cliente',
    ips: (g?.ips || []).join(', '),
  };
};

const gera = async () => {
  // eslint-disable-next-line no-alert
  if (
    atual.value.gateway &&
    !window.confirm(t('NEUXS_NEXUS.GATEWAY.CONFIRMA_ROTACIONAR'))
  )
    return;
  agindo.value = 'gerar';
  try {
    const { data } = await api.value.geraGateway({
      sgp_id: atual.value.id,
      ...formulario.value,
    });
    criado.value = data;
    formulario.value = null;
    await carrega();
  } catch (error) {
    useAlert(erroDaApi(error, t('NEUXS_NEXUS.GATEWAY.ERRO_ACAO')));
  } finally {
    agindo.value = '';
  }
};

const revoga = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('NEUXS_NEXUS.GATEWAY.CONFIRMA_REVOGAR'))) return;
  agindo.value = 'revogar';
  try {
    await api.value.revogaGateway(atual.value.id);
    await carrega();
  } catch (error) {
    useAlert(erroDaApi(error, t('NEUXS_NEXUS.GATEWAY.ERRO_ACAO')));
  } finally {
    agindo.value = '';
  }
};

const alternaEnvio = async modelo => {
  const pergunta = t('NEUXS_NEXUS.GATEWAY.CONFIRMA_LIGAR', {
    chave: modelo.chave,
  });
  // eslint-disable-next-line no-alert
  if (!modelo.envio_ativo && !window.confirm(pergunta)) return;
  agindo.value = `envio-${modelo.id}`;
  try {
    await api.value.alternaEnvio(modelo.id);
    await carrega();
  } catch (error) {
    useAlert(erroDaApi(error, t('NEUXS_NEXUS.GATEWAY.ERRO_ACAO')));
  } finally {
    agindo.value = '';
  }
};

const apaga = async modelo => {
  const pergunta = t('NEUXS_NEXUS.GATEWAY.CONFIRMA_APAGAR', {
    chave: modelo.chave,
  });
  // eslint-disable-next-line no-alert
  if (!window.confirm(pergunta)) return;
  agindo.value = `apagar-${modelo.id}`;
  try {
    await api.value.apagaModelo(modelo.id);
    await carrega();
  } catch (error) {
    useAlert(erroDaApi(error, t('NEUXS_NEXUS.GATEWAY.ERRO_ACAO')));
  } finally {
    agindo.value = '';
  }
};

const salvouModelo = async () => {
  editando.value = null;
  await carrega();
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <p class="text-sm text-n-slate-11">
      {{ t('NEUXS_NEXUS.GATEWAY.EXPLICA') }}
    </p>

    <div v-if="carregando" class="flex justify-center py-10 text-n-slate-11">
      <Spinner />
    </div>
    <p v-else-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</p>
    <p v-else-if="!integracoes.length" class="text-sm text-n-slate-11">
      {{ t('NEUXS_NEXUS.GATEWAY.SEM_INTEGRACAO') }}
    </p>

    <template v-else>
      <div v-if="integracoes.length > 1" class="flex flex-wrap gap-1">
        <Button
          v-for="integracao in integracoes"
          :key="integracao.id"
          :label="integracao.nome"
          size="xs"
          color="slate"
          :variant="integracao.id === escolhida ? 'solid' : 'ghost'"
          @click="
            escolhida = integracao.id;
            formulario = null;
            editando = null;
          "
        />
      </div>

      <!-- O endereço recém-gerado: aparece uma vez só. -->
      <section
        v-if="criado"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-amber-5 bg-n-amber-2"
      >
        <p class="text-sm text-n-amber-11">
          {{ t('NEUXS_NEXUS.GATEWAY.COPIE_AGORA') }}
        </p>
        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('NEUXS_NEXUS.GATEWAY.CONFIG_SGP') }}
            </span>
            <Button
              :label="
                copiado === 'config'
                  ? t('NEUXS_NEXUS.COPIADO')
                  : t('NEUXS_NEXUS.COPIAR')
              "
              size="xs"
              variant="faded"
              @click="copia(criado.config_sgp, 'config')"
            />
          </div>
          <pre
            class="p-3 overflow-x-auto text-xs rounded-lg bg-n-alpha-2 text-n-slate-12"
            >{{ criado.config_sgp }}</pre
          >
        </div>
        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('NEUXS_NEXUS.GATEWAY.SO_ENDERECO') }}
            </span>
            <Button
              :label="
                copiado === 'endereco'
                  ? t('NEUXS_NEXUS.COPIADO')
                  : t('NEUXS_NEXUS.COPIAR')
              "
              size="xs"
              variant="faded"
              @click="copia(criado.endereco, 'endereco')"
            />
          </div>
          <code
            class="p-3 text-xs break-all rounded-lg bg-n-alpha-2 text-n-slate-12"
          >
            {{ criado.endereco }}
          </code>
        </div>
        <div>
          <Button
            :label="t('NEUXS_NEXUS.GATEWAY.FECHAR')"
            size="sm"
            @click="criado = null"
          />
        </div>
      </section>

      <section
        v-if="atual"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak"
      >
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="text-heading-3 text-n-slate-12">
            {{ t('NEUXS_NEXUS.GATEWAY.ENDERECO') }}
          </span>
          <div v-if="!formulario" class="flex gap-2">
            <Button
              :label="
                atual.gateway
                  ? t('NEUXS_NEXUS.GATEWAY.ROTACIONAR')
                  : t('NEUXS_NEXUS.GATEWAY.GERAR')
              "
              icon="i-lucide-refresh-cw"
              size="sm"
              @click="abreFormulario"
            />
            <Button
              v-if="atual.gateway"
              :label="t('NEUXS_NEXUS.GATEWAY.REVOGAR')"
              size="sm"
              variant="ghost"
              color="ruby"
              :is-loading="agindo === 'revogar'"
              @click="revoga"
            />
          </div>
        </div>

        <div
          v-if="atual.gateway && !formulario"
          class="flex flex-col gap-1 text-sm text-n-slate-11"
        >
          <span>
            {{
              t('NEUXS_NEXUS.GATEWAY.REFERENCIA', {
                ref: atual.gateway.referencia,
              })
            }}
            ·
            <code>{{ `set_to=${atual.gateway.param_to}` }}</code>
            <code class="ml-1">{{ `set_msg=${atual.gateway.param_msg}` }}</code>
            <code class="ml-1">
              {{ `set_contrato=${atual.gateway.param_contrato}` }}
            </code>
          </span>
          <span>
            {{ t('NEUXS_NEXUS.GATEWAY.USOS', { n: atual.gateway.usos }) }}
            ·
            {{
              atual.gateway.ultimo_uso_em
                ? t('NEUXS_NEXUS.GATEWAY.ULTIMO_USO', {
                    quando: quando(atual.gateway.ultimo_uso_em),
                  })
                : t('NEUXS_NEXUS.GATEWAY.NUNCA_USADO')
            }}
          </span>
          <span>
            {{
              atual.gateway.ips.length
                ? t('NEUXS_NEXUS.GATEWAY.IPS', {
                    ips: atual.gateway.ips.join(', '),
                  })
                : t('NEUXS_NEXUS.GATEWAY.QUALQUER_IP')
            }}
          </span>
        </div>
        <p v-else-if="!formulario" class="text-sm text-n-slate-11">
          {{ t('NEUXS_NEXUS.GATEWAY.SEM_ENDERECO') }}
        </p>

        <form
          v-if="formulario"
          class="grid gap-3 md:grid-cols-2"
          @submit.prevent="gera"
        >
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('NEUXS_NEXUS.GATEWAY.PARAM_TO') }}
            <input v-model="formulario.param_to" type="text" class="!mb-0" />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('NEUXS_NEXUS.GATEWAY.PARAM_MSG') }}
            <input v-model="formulario.param_msg" type="text" class="!mb-0" />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('NEUXS_NEXUS.GATEWAY.PARAM_CONTRATO') }}
            <input
              v-model="formulario.param_contrato"
              type="text"
              class="!mb-0"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('NEUXS_NEXUS.GATEWAY.IPS_CAMPO') }}
            <input v-model="formulario.ips" type="text" class="!mb-0" />
          </label>
          <div class="flex gap-2 md:col-span-2">
            <Button
              type="submit"
              :label="t('NEUXS_NEXUS.GATEWAY.CONFIRMAR')"
              size="sm"
              :is-loading="agindo === 'gerar'"
            />
            <Button
              :label="t('NEUXS_NEXUS.GATEWAY.CANCELAR')"
              size="sm"
              variant="ghost"
              color="slate"
              @click="formulario = null"
            />
          </div>
        </form>
      </section>

      <section v-if="atual" class="flex flex-col gap-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="text-heading-3 text-n-slate-12">
            {{ t('NEUXS_NEXUS.GATEWAY.MODELOS') }}
          </span>
          <Button
            v-if="!editando"
            :label="t('NEUXS_NEXUS.GATEWAY.NOVO_MODELO')"
            icon="i-lucide-plus"
            size="sm"
            @click="editando = { sgp_id: atual.id }"
          />
        </div>
        <p class="text-sm text-n-slate-11">
          {{ t('NEUXS_NEXUS.GATEWAY.MODELOS_EXPLICA') }}
        </p>

        <ModeloEditor
          v-if="editando"
          :account-id="accountId"
          :modelo="editando"
          :templates="templates"
          @salvo="salvouModelo"
          @cancelar="editando = null"
        />

        <p v-if="!atual.modelos.length" class="text-sm text-n-slate-11">
          {{ t('NEUXS_NEXUS.GATEWAY.SEM_MODELOS') }}
        </p>
        <ul v-else class="flex flex-col divide-y divide-n-weak">
          <li
            v-for="modelo in atual.modelos"
            :key="modelo.id"
            class="flex flex-col gap-1 py-3"
          >
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                #{{ modelo.chave }}
              </span>
              <span
                class="px-2 text-xs rounded-full"
                :class="
                  !modelo.ativo
                    ? 'bg-n-slate-3 text-n-slate-10'
                    : modelo.envio_ativo
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-amber-3 text-n-amber-11'
                "
              >
                {{
                  !modelo.ativo
                    ? t('NEUXS_NEXUS.GATEWAY.DESLIGADO')
                    : modelo.envio_ativo
                      ? t('NEUXS_NEXUS.GATEWAY.ENVIANDO')
                      : t('NEUXS_NEXUS.GATEWAY.ENSAIO')
                }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ modelo.caixa }} · {{ modelo.template_nome }} ({{
                  modelo.template_idioma
                }})
              </span>
            </div>
            <p v-if="modelo.problema" class="text-xs text-n-ruby-11">
              {{ modelo.problema }}
            </p>
            <div class="flex flex-wrap items-center gap-2">
              <code class="text-xs break-all text-n-slate-11">
                {{ modelo.texto_sgp }}
              </code>
              <Button
                :icon="
                  copiado === `texto-${modelo.id}`
                    ? 'i-lucide-check'
                    : 'i-lucide-copy'
                "
                size="xs"
                variant="ghost"
                color="slate"
                :title="t('NEUXS_NEXUS.COPIAR')"
                @click="copia(modelo.texto_sgp, `texto-${modelo.id}`)"
              />
            </div>
            <div class="flex flex-wrap gap-1">
              <Button
                :label="
                  modelo.envio_ativo
                    ? t('NEUXS_NEXUS.GATEWAY.DESLIGAR')
                    : t('NEUXS_NEXUS.GATEWAY.LIGAR')
                "
                size="xs"
                :variant="modelo.envio_ativo ? 'faded' : 'solid'"
                :disabled="!modelo.ativo && !modelo.envio_ativo"
                :is-loading="agindo === `envio-${modelo.id}`"
                @click="alternaEnvio(modelo)"
              />
              <Button
                :label="t('NEUXS_NEXUS.GATEWAY.EDITAR')"
                size="xs"
                variant="ghost"
                color="slate"
                @click="editando = modelo"
              />
              <Button
                :label="t('NEUXS_NEXUS.GATEWAY.APAGAR')"
                size="xs"
                variant="ghost"
                color="ruby"
                :is-loading="agindo === `apagar-${modelo.id}`"
                @click="apaga(modelo)"
              />
            </div>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>
